<template>
    <hts-drawer
        v-model="visible"
        title="Chi tiết đặt xe"
        :size="70"
        @close="close">
        <div
            v-if="bookingData"
            :style="{ minWidth: minWidthScroll }">
            <div class="font-bold ml-3 my-3 flex space-x-1 items-center">
                <span>
                    Trạng thái:
                </span>
                <span
                    :class="{
                        'text-gray-800': bookingData.status === 1,
                        'text-info-800': bookingData.status === 2,
                        'text-warning-800': bookingData.status === 3,
                        'text-success-800': bookingData.status === 4,
                        'text-error-800': bookingData.status === -1 || bookingData.status === 0
                    }">
                    {{ getStatusText(bookingData.status) }}
                </span>
                <span
                    v-if="bookingData.status === -1 && bookingData.reasonCancelLabel"
                    class="ml-2 text-sm text-error-600">
                    ({{ bookingData.reasonCancelLabel }})
                </span>
                <span
                    v-if="bookingData.status === 0 && bookingData.reasonRejectLabel"
                    class="ml-2 text-sm text-orange-600">
                    ({{ bookingData.reasonRejectLabel }})
                </span>
            </div>

            <hts-card title="Thông tin chung">
                <div class="grid grid-cols-2 gap-4">
                    <hts-form-input
                        v-model="bookingData.bookingNumber"
                        label="Mã đặt xe"
                        readonly />
                    <hts-form-input
                        :model-value="formatDate(bookingData.createdAt)"
                        label="Ngày đặt"
                        readonly />
                    <hts-form-input
                        v-if="bookingData.bookingQuantity"
                        :model-value="bookingData.bookingQuantity"
                        label="Số lượng"
                        readonly />
                </div>
            </hts-card>

            <hts-card title="Thông tin vận chuyển">
                <div class="grid grid-cols-2 gap-4">
                    <hts-form-input
                        v-model="bookingData.originName"
                        label="Điểm đi"
                        readonly />
                    <hts-form-input
                        v-model="bookingData.destinationName"
                        label="Điểm đến"
                        readonly />
                    <hts-form-input
                        :model-value="bookingData.transportation === 'CONTAINER' ? 'Container' : 'Xe tải'"
                        label="Phương tiện"
                        readonly />
                    <hts-form-input
                        :model-value="getVehicleTypeText(bookingData)"
                        label="Loại xe"
                        readonly />
                    <hts-form-input
                        v-if="bookingData.date"
                        v-model="bookingData.date"
                        label="Ngày vận chuyển"
                        readonly />
                    <hts-form-input
                        v-if="bookingData.shipmentType"
                        :model-value="bookingData.shipmentType === 'export' ? 'Xuất khẩu' : 'Nhập khẩu'"
                        label="Loại vận chuyển"
                        readonly />
                </div>
            </hts-card>

            <hts-card title="Thông tin hàng hóa">
                <div class="grid grid-cols-2 gap-4">
                    <hts-form-input
                        :model-value="bookingData.commodityName"
                        label="Hàng hóa"
                        readonly />
                    <hts-form-input
                        v-model="bookingData.goodsWeight"
                        label="Trọng lượng (kg)"
                        readonly />
                    <hts-form-textarea
                        v-if="bookingData.goodsDescription"
                        v-model="bookingData.goodsDescription"
                        label="Mô tả hàng hóa"
                        class="col-span-2"
                        readonly />
                </div>
            </hts-card>

            <hts-card title="Thông tin liên hệ">
                <div class="grid grid-cols-2 gap-4">
                    <hts-form-input
                        v-model="bookingData.contactName"
                        label="Người liên hệ"
                        readonly />
                    <hts-form-input
                        v-model="bookingData.contactPhone"
                        label="Số điện thoại"
                        readonly />
                    <hts-form-input
                        v-if="bookingData.contactEmail"
                        v-model="bookingData.contactEmail"
                        label="Email"
                        readonly />
                </div>
            </hts-card>

            <hts-card
                v-if="bookingData.note"
                title="Ghi chú">
                <hts-form-textarea
                    v-model="bookingData.note"
                    readonly />
            </hts-card>

            <hts-card title="Thông tin giá">
                <div class="grid grid-cols-2 gap-4">
                    <div>
                        <div class="text-xl font-bold">
                            Giá: {{ $formatters.formatCurrency(bookingData.priceInfo?.price || bookingData.price) }} VNĐ
                        </div>
                    </div>
                    <div>
                        <div class="text-lg font-semibold text-blue-600">
                            Tổng cộng: {{ $formatters.formatCurrency(bookingData.totalPrice || bookingData.price * bookingData.bookingQuantity) }} VNĐ
                        </div>
                        <div class="text-sm text-gray-600 mt-2">
                            Số lượng: {{ bookingData.bookingQuantity }}
                        </div>
                    </div>
                </div>
            </hts-card>
        </div>
    </hts-drawer>
</template>

<script setup lang="ts">
import { ref } from 'vue'

const visible = ref(false)
const bookingData = ref<any>(null)
const minWidthScroll = '600px'

// Danh sách loại hàng hóa
const goodsTypes = [
  { value: 'container', label: 'Container' },
  { value: 'pallet', label: 'Pallet' },
  { value: 'bulk', label: 'Hàng rời' },
  { value: 'fragile', label: 'Hàng dễ vỡ' },
  { value: 'frozen', label: 'Hàng đông lạnh' }
]

// Lấy label của loại hàng hóa
const getGoodsTypeLabel = (value: any) => {
  const found = goodsTypes.find((item) => item.value === value)
  return found ? found.label : value
}

// Lấy text trạng thái
const getStatusText = (status: any) => {
  const statusMap = {
    '-1': 'Đã hủy',
    '0': 'Từ chối',
    '1': 'Chờ xác nhận',
    '2': 'Đã xác nhận',
    '3': 'Đang vận chuyển',
    '4': 'Hoàn thành'
  } as { [key: string]: string }
  return statusMap[status?.toString()] || status
}

// Format date
const formatDate = (dateString: string) => {
  if (!dateString) {
    return ''
  }
  return new Date(dateString).toLocaleString('vi-VN')
}

// Lấy text loại xe
const getVehicleTypeText = (booking: any) => {
  if (!booking) {
    return ''
  }

  if (booking.transportation === 'CONTAINER') {
    return booking.containerType || ''
  } else {
    return booking.weightCargo ? `${booking.weightCargo} kg` : ''
  }
}

// Mở drawer chi tiết
const open = (item: any) => {
  bookingData.value = item
  visible.value = true
}

// Đóng drawer
const close = () => {
  visible.value = false

  setTimeout(() => {
    bookingData.value = null
  }, 300)
}

// Expose methods
defineExpose({
  open
})
</script>
